/**
 * Favourite international cricket teams (profile grid). Logos at
 * `favs/cricket_int_teams/{slug}.png` on cricket8-web-content.
 * Add matching PNGs for any new slug (same pixel style as existing tiles).
 */
export const PROFILE_FAV_CRICKET_INT_LOGO_BASE =
  "https://cricket8-web-content.s3.us-east-1.amazonaws.com/favs/cricket_int_teams/";

/** Alphabetical by display name. */
export const PROFILE_FAV_CRICKET_INT_OPTIONS = [
  { slug: "afghanistan", label: "Afghanistan" },
  { slug: "australia", label: "Australia" },
  { slug: "bangladesh", label: "Bangladesh" },
  { slug: "england", label: "England" },
  { slug: "india", label: "India" },
  { slug: "ireland", label: "Ireland" },
  { slug: "nepal", label: "Nepal" },
  { slug: "netherlands", label: "Netherlands" },
  { slug: "new-zealand", label: "New Zealand" },
  { slug: "pakistan", label: "Pakistan" },
  { slug: "scotland", label: "Scotland" },
  { slug: "south-africa", label: "South Africa" },
  { slug: "sri-lanka", label: "Sri Lanka" },
  { slug: "uae", label: "UAE" },
  { slug: "west-indies", label: "West Indies" },
  { slug: "zimbabwe", label: "Zimbabwe" },
] as const;

export type ProfileFavCricketIntSlug = (typeof PROFILE_FAV_CRICKET_INT_OPTIONS)[number]["slug"];

const OPTION_SLUGS = PROFILE_FAV_CRICKET_INT_OPTIONS.map((t) => t.slug);
const SLUG_SET = new Set<string>(OPTION_SLUGS);
const SLUG_TO_LABEL: Record<string, string> = Object.fromEntries(
  PROFILE_FAV_CRICKET_INT_OPTIONS.map((t) => [t.slug, t.label])
);

export function cricketIntTeamLogoUrl(slug: string): string {
  return `${PROFILE_FAV_CRICKET_INT_LOGO_BASE}${encodeURIComponent(slug)}.png`;
}

/** Light crest on transparent PNG — use a white tile so it reads on purple UI. */
export function cricketIntLogoUsesWhiteTile(slug: string): boolean {
  return (
    slug === "england" ||
    slug === "new-zealand" ||
    slug === "scotland" ||
    slug === "pakistan" ||
    slug === "nepal"
  );
}

export function parseFavCricketIntFromStorage(raw: string | undefined | null): string[] {
  if (!raw?.trim()) return [];
  const parts = raw
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ordered: string[] = [];
  for (const slug of OPTION_SLUGS) {
    if (parts.includes(slug)) ordered.push(slug);
  }
  for (const p of parts) {
    if (!SLUG_SET.has(p) && !ordered.includes(p)) ordered.push(p);
  }
  return ordered;
}

export function serializeFavCricketIntToStorage(selected: readonly string[]): string {
  if (selected.length === 0) return "";
  const set = new Set(selected);
  const known = OPTION_SLUGS.filter((s) => set.has(s));
  const unknown = selected.filter((s) => !SLUG_SET.has(s));
  return [...known, ...unknown].join(", ");
}

export function favCricketIntLabel(slug: string): string {
  return SLUG_TO_LABEL[slug] ?? slug.replace(/-/g, " ");
}
